import React, { useState, useEffect } from "react";
import { db, collection, onSnapshot, updateDoc, doc, fmt, fmtDate, statusBadge } from "../config/firebase";
import "./AdminComponents.css";

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(
      collection(db, "orders"),
      (snap) => {
        const items = [];
        snap.forEach((docSnap) => {
          items.push({ docId: docSnap.id, ...docSnap.data() });
        });
        items.sort((a, b) => {
          const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
          const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
          return bTime - aTime;
        });
        setOrders(items);
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching orders:", err);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  const handleStatusChange = async (docId, newStatus) => {
    try {
      setUpdatingId(docId);
      await updateDoc(doc(db, "orders", docId), { status: newStatus });
    } catch (err) {
      console.error("Error updating status:", err);
    } finally {
      setUpdatingId(null);
    }
  };

  const markSeen = async (docId) => {
    try {
      await updateDoc(doc(db, "orders", docId), { notified: true });
    } catch (err) {
      console.error("Error marking order as seen:", err);
    }
  };

  const toggleExpand = (docId) => {
    setExpandedId(expandedId === docId ? null : docId);
  };

  const statusOptions = ["Pending", "Paid", "Completed", "Cancelled"];

  // NEW: helper to get a "day key" (e.g. "04 Jul 2026") for grouping
  const getDayKey = (order) => {
    let dateObj;
    if (order.createdAt?.toDate) {
      dateObj = order.createdAt.toDate();
    } else if (order.createdAt?.seconds) {
      dateObj = new Date(order.createdAt.seconds * 1000);
    } else if (typeof order.createdAt === "string") {
      dateObj = new Date(order.createdAt);
    } else {
      return "Unknown Date";
    }
    return dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  // NEW: group orders by day, preserving the existing sort (newest first)
  const groupedOrders = orders.reduce((groups, order) => {
    const key = getDayKey(order);
    if (!groups[key]) groups[key] = [];
    groups[key].push(order);
    return groups;
  }, {});

  // NEW: maintain day order as they appear (already sorted newest-first from the query)
  const dayKeys = Object.keys(groupedOrders).sort((a, b) => {
    const aOrder = groupedOrders[a][0];
    const bOrder = groupedOrders[b][0];
    const aTime = aOrder.createdAt?.toMillis ? aOrder.createdAt.toMillis() : 0;
    const bTime = bOrder.createdAt?.toMillis ? bOrder.createdAt.toMillis() : 0;
    return bTime - aTime;
  });

  // NEW: calculate day totals — counts money actually received via Razorpay (order.advance)
  const getDaySummary = (dayOrders) => {
    const totalOrders = dayOrders.length;
    const totalSales = dayOrders.reduce((sum, o) => sum + (o.advance || 0), 0);
    return { totalOrders, totalSales };
  };

  return (
    <div className="admin-orders">
      <div className="admin-header-row">
        <h2 className="admin-section-title">All Orders</h2>
      </div>

      {loading ? (
        <div className="admin-loading">
          <div className="spinner"></div>
        </div>
      ) : orders.length === 0 ? (
        <div className="admin-empty">No orders yet.</div>
      ) : (
        dayKeys.map((dayKey) => {
          const dayOrders = groupedOrders[dayKey];
          const { totalOrders, totalSales } = getDaySummary(dayOrders);
          return (
            <div key={dayKey} className="day-group">
              <div className="day-group-header">
                <span className="day-group-date">{dayKey}</span>
                <div className="day-group-stats">
                  <span className="day-stat-pill orders-pill">{totalOrders} order{totalOrders !== 1 ? "s" : ""}</span>
                  <span className="day-stat-pill sales-pill">{fmt(totalSales)} received</span>
                </div>
              </div>

              <div className="orders-table-wrapper">
                <table className="orders-table">
                  <thead>
                    <tr>
                      <th>Order ID</th>
                      <th>Customer</th>
                      <th>Items</th>
                      <th>Total</th>
                      <th>Paid</th>
                      <th>Status</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dayOrders.map((order) => {
                      const badge = statusBadge(order.status);
                      const isAdvance = order.paymentMode === "advance";
                      const isNew = order.notified === false;
                      return (
                        <React.Fragment key={order.docId}>
                          <tr style={isNew ? { background: "#fff8f0", borderLeft: "4px solid #FF6B35" } : {}}>
                            <td className="order-id">
                              {isNew && (
                                <span style={{
                                  display: "inline-block",
                                  background: "#FF6B35",
                                  color: "#fff",
                                  fontSize: "10px",
                                  fontWeight: 800,
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  marginRight: "8px",
                                  letterSpacing: "0.5px"
                                }}>
                                  NEW
                                </span>
                              )}
                              {order.id}
                            </td>
                            <td>
                              <div className="customer-info">
                                <span className="customer-name">{order.customerName || "N/A"}</span>
                                <span className="customer-phone">{order.customerPhone || "N/A"}</span>
                              </div>
                            </td>
                            <td>{order.items?.length || 0} items</td>
                            <td className="order-total">{fmt(order.total || 0)}</td>
                            <td>
                              <div className="paid-info">
                                <span className="paid-amount">{fmt(order.advance || 0)}</span>
                                {isAdvance && order.balanceDue > 0 && (
                                  <span className="balance-due">Bal: {fmt(order.balanceDue)}</span>
                                )}
                                {order.paymentMode && (
                                  <span className={`payment-mode-tag ${order.paymentMode}`}>
                                    {order.paymentMode === "advance" ? "30%" : "Full"}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className={`status-badge ${badge.class}`}>{badge.label}</span>
                            </td>
                            <td className="order-date">
                              {order.createdAt?.toDate
                                ? fmtDate(order.createdAt.toDate().toISOString())
                                : order.createdAt
                                ? fmtDate(typeof order.createdAt === "string" ? order.createdAt : new Date(order.createdAt.seconds * 1000).toISOString())
                                : "N/A"}
                            </td>
                            <td>
                              <div className="order-actions">
                                <select
                                  value={order.status}
                                  onChange={(e) => handleStatusChange(order.docId, e.target.value)}
                                  disabled={updatingId === order.docId}
                                  className="status-select"
                                >
                                  {statusOptions.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                                {isNew && (
                                  <button
                                    onClick={() => markSeen(order.docId)}
                                    style={{
                                      padding: "6px 12px",
                                      background: "#00FF88",
                                      color: "#2D2D2D",
                                      border: "none",
                                      borderRadius: "8px",
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      cursor: "pointer",
                                      whiteSpace: "nowrap"
                                    }}
                                  >
                                    ✓ OK
                                  </button>
                                )}
                                <button className="btn-expand" onClick={() => toggleExpand(order.docId)}>
                                  {expandedId === order.docId ? "▲" : "▼"}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {expandedId === order.docId && (
                            <tr className="order-detail-row">
                              <td colSpan="8">
                                <div className="order-details">
                                  <h4>Order Items</h4>
                                  <div className="items-list">
                                    {order.items?.map((item, idx) => (
                                      <div key={idx} className="detail-item">
                                        <span className="detail-name">{item.productName}</span>
                                        {item.variantLabel && (
                                          <span className="detail-variant">{item.variantLabel}</span>
                                        )}
                                        <span className="detail-qty">× {item.quantity}</span>
                                        <span className="detail-price">{fmt(item.price * item.quantity)}</span>
                                      </div>
                                    ))}
                                  </div>
                                  <div className="detail-summary">
                                    <div><span>Subtotal:</span> <span>{fmt(order.subtotal || 0)}</span></div>
                                    {order.packagingCharge != null && (
                                      <div><span>Packaging:</span> <span>{fmt(order.packagingCharge)}</span></div>
                                    )}
                                    <div><span>Payment Mode:</span> <span className="detail-mode">{order.paymentMode === "advance" ? "30% Advance" : "Full Payment"}</span></div>
                                    <div><span>Paid Now:</span> <span>{fmt(order.advance || 0)}</span></div>
                                    {order.balanceDue > 0 && (
                                      <div><span>Balance Due:</span> <span className="detail-balance">{fmt(order.balanceDue)}</span></div>
                                    )}
                                    <div><span>Total:</span> <span className="detail-total">{fmt(order.total || 0)}</span></div>
                                    {order.paymentId && (
                                      <div><span>Payment ID:</span> <span className="payment-id">{order.paymentId}</span></div>
                                    )}
                                    {order.notes && (
                                      <div><span>Notes:</span> <span>{order.notes}</span></div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
};

export default AdminOrders;